/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useMemo } from "react";

import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../viewModel";
import {
  alertOverlayActionSelected,
  dismissAlertOverlay,
  selectAlertOverlayActions,
  selectAlertOverlayMessage,
  selectAlertOverlayRetryPending,
  selectAlertOverlayTitle,
  selectAlertOverlayVariant,
  selectFocusedActionIndex,
  selectIsAlertOverlayVisible
} from "../../../viewModel/alertOverlay";
import { useModalController } from "../../hooks/useModalController";
import { AlertOverlayController } from "./AlertOverlayController";
import AlertOverlay from "./AlertOverlay";

/**
 * Connected container that reads alert overlay state from Redux
 * and renders the AlertOverlay when visible.
 *
 * Pushes an AlertOverlayController onto the active screen's modal stack
 * so all D-pad input is consumed by the overlay while it is open.
 */
const AlertOverlayContainer: FunctionComponent = (): JSX.Element | null => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
  const TAG: string = "AlertOverlayContainer";
  const dispatch = useUnityDispatch();

  const isVisible = useUnitySelector(selectIsAlertOverlayVisible);
  const title = useUnitySelector(selectAlertOverlayTitle);
  const message = useUnitySelector(selectAlertOverlayMessage);
  const actions = useUnitySelector(selectAlertOverlayActions);
  const focusedActionIndex = useUnitySelector(selectFocusedActionIndex);
  const retryPending = useUnitySelector(selectAlertOverlayRetryPending);
  const variant = useUnitySelector(selectAlertOverlayVariant);

  const modalController = useMemo(
    (): AlertOverlayController =>
      new AlertOverlayController(dispatch, UnityAppStore.getState),
    [dispatch]
  );

  useModalController(modalController, isVisible);

  const handleAction = useCallback(
    (actionId: string): void => {
      const selected = actions.find(
        (action): boolean => action.id === actionId
      );
      // A keeps-open action (Retry) records the selection but leaves the
      // overlay up so its loading state shows; everything else dismisses.
      if (selected !== undefined && selected.keepsOverlayOpen === true) {
        dispatch(alertOverlayActionSelected(actionId));
        return;
      }
      dispatch(dismissAlertOverlay(actionId));
    },
    [actions, dispatch]
  );

  if (isVisible !== true) {
    return null;
  }

  return (
    <AlertOverlay
      title={title}
      message={message}
      actions={actions}
      focusedActionIndex={focusedActionIndex}
      retryPending={retryPending}
      onAction={handleAction}
      variant={variant}
    />
  );
};

export default AlertOverlayContainer;
